import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import {
  Building2, Users, TrendingUp, Calendar, ChevronDown, CheckCircle2
} from "lucide-react";

const timeline = [
  { year: "2013", event: "Ouverture de la crèche au quartier Kemkian — 2 bâtiments construits (crèche 85m² + salle de cours 60m²)" },
  { year: "2017", event: "Construction d'un mur d'enceinte (140m × 65m × 2,10m) pour sécuriser enfants et bâtiments" },
  { year: "2018", event: "Ouverture d'une classe de CP1" },
  { year: "2019", event: "Ouverture d'une classe de CP2" },
  { year: "2020", event: "Ouverture d'une classe de CE1" },
  { year: "2021–2022", event: "Construction de 4 salles de classe supplémentaires grâce à La Guilde, Fondation Devoteam et Bolloré" },
  { year: "2022–2023", event: "Ouverture des classes CE2 et CM1 — 143 élèves inscrits" },
  { year: "2023–2024", event: "Croissance : 240 élèves inscrits (112 filles, 128 garçons)" },
  { year: "2024–2025", event: "312 élèves inscrits — Démarrage du projet Amélioration (Mars 2025)" },
];

const currentProject = [
  "Construction d'un bâtiment de 2 salles équipées en tables-bancs",
  "Clôture séparant maternelle et primaire",
  "2 latrines à 2 compartiments avec fosses septiques",
  "Ameublement de la maternelle (tables, chaises, tableaux)",
];

const funders2021 = [
  { name: "La Guilde AMP", amount: "10 000 €" },
  { name: "Fondation Devoteam", amount: "6 000 €" },
  { name: "Mécénat Bolloré", amount: "5 000 €" },
  { name: "Apport APPE", amount: "5 000 €" },
];

const KemkianEcole = () => {
  const [timelineOpen, setTimelineOpen] = useState(false);

  return (
    <section id="projet-kemkian" className="section scroll-mt-20 bg-light">
      <div className="container mx-auto">

        {/* Header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4">
            Projet 1 — Kemkian, Sarh, Tchad
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            Une école dans un quartier défavorisé
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            Depuis 2013, nous avons transformé un quartier périphérique de Sarh en créant une crèche
            et une école primaire qui accueille aujourd'hui plus de 312 élèves.
          </motion.p>
        </motion.div>

        {/* Stats row */}
        <motion.div
          variants={staggerContainer(0.1, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
        >
          {[
            { icon: <Users size={20} />, value: "312", label: "Élèves (2024–25)", color: "bg-primary/10 text-primary" },
            { icon: <Building2 size={20} />, value: "4", label: "Salles de classe", color: "bg-secondary/10 text-secondary" },
            { icon: <TrendingUp size={20} />, value: "+118%", label: "Croissance effectifs", color: "bg-accent/10 text-accent" },
            { icon: <Calendar size={20} />, value: "2013", label: "Année de création", color: "bg-purple-100 text-purple-700" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              variants={fadeIn("up", i * 0.05)}
              className="card p-6 text-center"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 ${stat.color}`}>
                {stat.icon}
              </div>
              <div className="font-display font-bold text-3xl text-dark mb-1">{stat.value}</div>
              <div className="text-grey text-xs font-medium uppercase tracking-wide">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Current project highlight */}
        <motion.div
          variants={fadeIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid lg:grid-cols-2 gap-10 mb-16"
        >
          {/* Left — context */}
          <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-accent font-semibold text-sm uppercase tracking-wide">En cours — 2024/2025</span>
            </div>
            <h3 className="h3 mb-4">Amélioration des conditions scolaires</h3>
            <p className="text-grey leading-relaxed mb-6 text-sm">
              Malgré la forte croissance des effectifs, les CM2 doivent se contenter d'un hangar en paille.
              Notre projet 2024–2025 vise à offrir des conditions dignes à tous les élèves.
            </p>
            <div className="space-y-3">
              {currentProject.map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <CheckCircle2 size={16} className="text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-dark text-sm">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
              <p className="text-sm text-grey">
                <strong className="text-primary">Démarré en mars 2025</strong> — Construction par l'entreprise EBTPS,
                sous supervision de l'AME et d'un bénévole contrôleur.
                Priorité donnée au bâtiment avant la saison des pluies.
              </p>
            </div>
          </div>

          {/* Right — evolution des effectifs */}
          <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
            <h3 className="font-heading font-bold text-xl text-dark mb-6">Évolution des effectifs</h3>
            <div className="space-y-4">
              {[
                { year: "2022–2023", total: 143, filles: 65, garcons: 78, max: 312 },
                { year: "2023–2024", total: 240, filles: 112, garcons: 128, max: 312 },
                { year: "2024–2025", total: 312, filles: null, garcons: null, max: 312 },
              ].map((row, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-sm font-medium text-dark">{row.year}</span>
                    <span className="text-sm font-bold text-primary">{row.total} élèves</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(row.total / row.max) * 100}%` }}
                      transition={{ duration: 1, delay: i * 0.15, ease: "easeOut" }}
                      viewport={{ once: true }}
                      className="h-full rounded-full bg-gradient-to-r from-primary to-secondary"
                    />
                  </div>
                  {row.filles && (
                    <div className="flex gap-4 mt-1">
                      <span className="text-xs text-grey">{row.filles} filles</span>
                      <span className="text-xs text-grey">{row.garcons} garçons</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Funders */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <h4 className="font-semibold text-sm text-dark mb-4">Financements 2020–2022</h4>
              <div className="grid grid-cols-2 gap-2">
                {funders2021.map((f, i) => (
                  <div key={i} className="bg-light rounded-lg px-3 py-2">
                    <div className="text-xs text-grey">{f.name}</div>
                    <div className="font-semibold text-sm text-dark">{f.amount}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Timeline accordion */}
        <motion.div
          variants={fadeIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
        >
          <button
            onClick={() => setTimelineOpen(!timelineOpen)}
            className="w-full flex items-center justify-between p-6 lg:p-8 text-left hover:bg-gray-50 transition-colors"
          >
            <div>
              <div className="font-display font-bold text-lg text-dark">Historique complet du projet</div>
              <div className="text-grey text-sm mt-1">2013 → 2025 — Toutes les étapes de la construction</div>
            </div>
            <ChevronDown
              size={20}
              className={`text-primary transition-transform duration-300 flex-shrink-0 ml-4 ${timelineOpen ? "rotate-180" : ""}`}
            />
          </button>

          <AnimatePresence>
            {timelineOpen && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 lg:px-8 pb-8">
                  <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary to-secondary" />
                    <div className="space-y-6 pl-12">
                      {timeline.map((t, i) => (
                        <div key={i} className="relative">
                          <div className="absolute -left-8 top-1 w-4 h-4 rounded-full bg-white border-2 border-primary" />
                          <div className="font-display font-semibold text-secondary text-sm mb-1">{t.year}</div>
                          <div className="text-dark text-sm leading-relaxed">{t.event}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

      </div>
    </section>
  );
};

export default KemkianEcole;
